import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { portalApi } from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Building2,
  MapPin,
  Calendar,
  Check,
  Clock,
  Image,
  FileText,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

export default function ClientPortal() {
  const { shareToken } = useParams();
  const { setForceLightMode } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    setForceLightMode(true);
    return () => setForceLightMode(false);
  }, [setForceLightMode]);

  useEffect(() => {
    fetchPortalData();
  }, [shareToken]);

  const fetchPortalData = async () => {
    try {
      const response = await portalApi.getProject(shareToken);
      // API returns project data directly with photos included
      setData(response.data);
    } catch (err) {
      setError('Project not found or link has expired');
    } finally {
      setLoading(false);
    }
  };

  const [gcTaskDraft, setGcTaskDraft] = useState({});   // phaseName -> string
  const [gcNoteDraft, setGcNoteDraft] = useState({});   // taskId -> string
  const [noteOpen, setNoteOpen] = useState({});         // taskId -> bool

  const gcAddTask = async (phaseName) => {
    const name = (gcTaskDraft[phaseName] || '').trim();
    if (!name) return;
    try {
      await portalApi.addTask(shareToken, phaseName, name);
      setGcTaskDraft((d) => ({ ...d, [phaseName]: '' }));
      fetchPortalData();
    } catch {
      setError('Could not add task');
    }
  };

  const gcAddNote = async (phaseName, taskId) => {
    const note = (gcNoteDraft[taskId] || '').trim();
    if (!note) return;
    try {
      await portalApi.addNote(shareToken, phaseName, taskId, note);
      setGcNoteDraft((d) => ({ ...d, [taskId]: '' }));
      setNoteOpen((o) => ({ ...o, [taskId]: false }));
      fetchPortalData();
    } catch {
      setError('Could not add note');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <span className="loading-spinner" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md rounded-sm">
          <CardContent className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-bold mb-2">Project Not Found</h2>
            <p className="text-muted-foreground">{error || 'Unable to load project data'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Data is the project object directly with photos included
  const project = data;
  const photos = data.photos || [];
  const invoices = data.invoices || [];
  const branding = data.branding || {};
  const isSmall = project.project_size === 'small';
  const tasks = project.tasks || [];
  const completedTasks = tasks.filter((t) => t.completed).length;
  const progress = isSmall
    ? (tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0)
    : (project.phases && project.phases.length > 0
        ? Math.round((project.phases.filter((p) => p.completed).length / project.phases.length) * 100)
        : 0);

  // Apply branding colors as CSS custom properties
  const brandColors = branding.colors || {};
  const headerStyle = {
    '--portal-primary': brandColors.primary || '#f97316',
    '--portal-secondary': brandColors.secondary || '#1e293b',
    background: `linear-gradient(135deg, ${brandColors.secondary || '#1e293b'} 0%, ${brandColors.primary || '#f97316'}20 100%)`
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-portal-page">
      {/* Header with Company Branding */}
      <div className="text-white py-12 px-4" style={headerStyle}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            {branding.custom_logo ? (
              <img 
                src={branding.custom_logo} 
                alt={branding.company_name || 'Company'} 
                className="h-10 max-w-[180px] object-contain"
                data-testid="portal-company-logo"
              />
            ) : (
              <>
                <Building2 
                  className="h-8 w-8" 
                  style={{ color: brandColors.primary || '#f97316' }} 
                  strokeWidth={1.5} 
                />
                <span className="text-xl font-bold">{branding.company_name || 'Corbel'}</span>
              </>
            )}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{project.name}</h1>
          {project.client_name && <p className="text-slate-300">Welcome, {project.client_name}</p>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Progress Card */}
        <Card className="rounded-sm border-t-4 border-t-orange-500">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h2 className="text-xl font-bold">Project Progress</h2>
                <p className="text-muted-foreground">
                  {isSmall
                    ? `${completedTasks} of ${tasks.length} ${tasks.length === 1 ? 'task' : 'tasks'} complete`
                    : `Current Phase: ${project.current_phase}`}
                </p>
              </div>
              <Badge
                className={
                  project.status === 'active'
                    ? 'bg-green-700 text-lg px-4 py-1'
                    : project.status === 'completed'
                    ? 'bg-slate-900 text-lg px-4 py-1'
                    : 'text-lg px-4 py-1'
                }
              >
                {project.status === 'active' ? 'In Progress' : project.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4">
              <Progress value={progress} className="h-4 flex-1" />
              <span className="text-2xl font-bold text-orange-500">{progress}%</span>
            </div>
          </CardContent>
        </Card>

        {/* Project Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-muted-foreground" />
                Project Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium">{project.address}</p>
              {(project.city || project.state) && (
                <p className="text-muted-foreground">
                  {project.city && <span>{project.city}, </span>}
                  {project.state}
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {project.start_date && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Started</span>
                  <span className="font-medium">
                    {new Date(project.start_date).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              )}
              {project.estimated_end_date && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Est. Completion</span>
                  <span className="font-medium">
                    {new Date(project.estimated_end_date).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Description */}
        {project.description && (
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg">Project Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{project.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Progress Tracker — Tasks (small) or Phases (standard) */}
        {isSmall ? (
          <Card className="rounded-sm" data-testid="portal-task-list">
            <CardHeader>
              <CardTitle className="text-lg">Project Checklist</CardTitle>
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  No tasks have been added yet. Check back soon for updates.
                </p>
              ) : (
                <div className="divide-y">
                  {tasks.map((task, index) => (
                    <div
                      key={task.id || index}
                      className="flex items-center gap-3 py-3"
                      data-testid={`portal-task-${index}`}
                    >
                      <div
                        className={`h-9 w-9 rounded-sm flex items-center justify-center flex-shrink-0 ${
                          task.completed ? 'bg-green-100' : 'bg-slate-100'
                        }`}
                      >
                        {task.completed ? (
                          <Check className="h-5 w-5 text-green-700" strokeWidth={2} />
                        ) : (
                          <Clock className="h-4 w-4 text-slate-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${task.completed ? 'text-muted-foreground line-through' : ''}`}>
                          {task.name}
                        </p>
                        {task.completed && task.completed_at && (
                          <p className="text-xs text-green-700">
                            Completed on{' '}
                            {new Date(task.completed_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg">Construction Phases</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="portal-phase-list">
                {project.phases.map((phase, index) => (
                  <div
                    key={phase.name}
                    className={`portal-phase-item ${
                      phase.completed ? 'completed' : phase.name === project.current_phase ? 'current' : ''
                    }`}
                    data-testid={`portal-phase-${index}`}
                  >
                    <div
                      className={`h-10 w-10 rounded-sm flex items-center justify-center flex-shrink-0 ${
                        phase.completed
                          ? 'bg-green-100'
                          : phase.name === project.current_phase
                          ? 'bg-orange-100'
                          : 'bg-slate-100'
                      }`}
                    >
                      {phase.completed ? (
                        <Check className="h-5 w-5 text-green-700" strokeWidth={2} />
                      ) : phase.name === project.current_phase ? (
                        <Clock className="h-5 w-5 text-orange-500" />
                      ) : (
                        <span className="text-sm font-bold text-slate-400">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{phase.name}</p>
                      {phase.completed && phase.completed_at && (
                        <p className="text-xs text-green-700">
                          Completed on{' '}
                          {new Date(phase.completed_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </p>
                      )}
                      {phase.name === project.current_phase && !phase.completed && (
                        <p className="text-xs text-orange-500">In Progress</p>
                      )}
                      {phase.notes && (
                        <p className="text-sm text-muted-foreground mt-1">{phase.notes}</p>
                      )}
                      {/* Phase tasks (with GC controls when enabled) */}
                      {((phase.tasks && phase.tasks.length > 0) || project.gc_enabled) && (
                        <div className="mt-2 space-y-1.5">
                          {(phase.tasks || []).map((task) => (
                            <div key={task.id} className="text-sm border-l-2 border-slate-200 pl-2" data-testid={`portal-task-${task.id}`}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={task.completed ? 'line-through text-muted-foreground' : ''}>{task.name}</span>
                                {task.added_by_gc && <span className="text-[10px] bg-slate-200 rounded-sm px-1">GC</span>}
                                {task.assignee_name && <span className="text-[11px] text-muted-foreground">· {task.assignee_name}</span>}
                                {task.due_date && <span className="text-[11px] text-muted-foreground">· due {task.due_date}</span>}
                                {task.completed && <Check className="h-3 w-3 text-green-600" />}
                              </div>
                              {(task.gc_notes || []).map((n) => (
                                <p key={n.id} className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5 mt-1">📝 {n.note}</p>
                              ))}
                              {project.gc_enabled && (
                                noteOpen[task.id] ? (
                                  <div className="flex gap-1 mt-1">
                                    <input
                                      value={gcNoteDraft[task.id] || ''}
                                      onChange={(e) => setGcNoteDraft((d) => ({ ...d, [task.id]: e.target.value }))}
                                      onKeyDown={(e) => e.key === 'Enter' && gcAddNote(phase.name, task.id)}
                                      placeholder="Note (e.g. needs redo)…"
                                      className="flex-1 text-xs border rounded-sm px-2 py-1"
                                      data-testid={`portal-note-input-${task.id}`}
                                    />
                                    <button onClick={() => gcAddNote(phase.name, task.id)} className="text-xs bg-slate-900 text-white rounded-sm px-2" data-testid={`portal-note-save-${task.id}`}>Add</button>
                                  </div>
                                ) : (
                                  <button onClick={() => setNoteOpen((o) => ({ ...o, [task.id]: true }))} className="text-[11px] text-orange-600 mt-0.5" data-testid={`portal-note-btn-${task.id}`}>+ Add note</button>
                                )
                              )}
                            </div>
                          ))}
                          {project.gc_enabled && (
                            <div className="flex gap-1 mt-1" data-testid={`portal-add-task-${index}`}>
                              <input
                                value={gcTaskDraft[phase.name] || ''}
                                onChange={(e) => setGcTaskDraft((d) => ({ ...d, [phase.name]: e.target.value }))}
                                onKeyDown={(e) => e.key === 'Enter' && gcAddTask(phase.name)}
                                placeholder="Add a task for this phase…"
                                className="flex-1 text-xs border rounded-sm px-2 py-1"
                                data-testid={`portal-add-task-input-${index}`}
                              />
                              <button onClick={() => gcAddTask(phase.name)} className="text-xs bg-orange-500 text-white rounded-sm px-2" data-testid={`portal-add-task-btn-${index}`}>Add</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invoices */}
        {invoices.length > 0 && (
          <Card className="rounded-sm" data-testid="portal-invoices">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-muted-foreground" />
                Invoices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {invoices.map((inv) => {
                  const due = inv.amount_due != null ? inv.amount_due : inv.total;
                  const paid = inv.status === 'paid';
                  return (
                    <div
                      key={inv.invoice_number}
                      className="flex items-center justify-between gap-3 py-3 flex-wrap"
                      data-testid={`portal-invoice-${inv.invoice_number}`}
                    >
                      <div>
                        <p className="font-medium">{inv.invoice_number}</p>
                        {inv.due_date && <p className="text-xs text-muted-foreground">Due {inv.due_date}</p>}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-semibold">{fmt(due)}</p>
                          {paid ? (
                            <Badge className="bg-green-700">Paid</Badge>
                          ) : (
                            <Badge variant="outline" className="capitalize">{inv.status}</Badge>
                          )}
                        </div>
                        {!paid && inv.public_token && (
                          <a href={`/pay/${inv.public_token}`} target="_blank" rel="noreferrer">
                            <Button size="sm" className="rounded-sm" data-testid={`portal-pay-${inv.invoice_number}`}>
                              View &amp; Pay
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Photos */}
        {photos && photos.length > 0 && (
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Image className="h-5 w-5 text-muted-foreground" />
                Project Photos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="photo-grid">
                {photos.map((photo) => (
                  <div
                    key={photo.id}
                    className="photo-card cursor-pointer"
                    onClick={() => setSelectedPhoto(photo)}
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${photo.image_url}`}
                      alt={photo.caption || 'Project photo'}
                    />
                    {(photo.caption || photo.phase) && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2">
                        {photo.phase && <Badge variant="outline" className="mr-2 text-white border-white/50">{photo.phase}</Badge>}
                        {photo.caption}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Photo Modal */}
        {selectedPhoto && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <div className="max-w-4xl max-h-[90vh] overflow-auto">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${selectedPhoto.image_url}`}
                alt={selectedPhoto.caption || 'Project photo'}
                className="w-full rounded-sm"
              />
              {(selectedPhoto.caption || selectedPhoto.phase) && (
                <div className="bg-white p-4 rounded-b-sm">
                  {selectedPhoto.phase && (
                    <Badge className="mb-2">{selectedPhoto.phase}</Badge>
                  )}
                  {selectedPhoto.caption && (
                    <p>{selectedPhoto.caption}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground py-8">
          <p>Powered by Corbel CRM</p>
          <p className="mt-1">Questions? Contact your project manager.</p>
        </div>
      </div>
    </div>
  );
}
